import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventsAPI } from '../api';
import { useTheme } from '../ThemeContext';
import ThemeToggle from '../components/ThemeToggle';
import UpgradeModal from '../components/UpgradeModal';
import './Events.css';

function NewEvent() {
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [sendThankYouSms, setSendThankYouSms] = useState(true);
  const [watermarkFile, setWatermarkFile] = useState(null);
  const [watermarkPreview, setWatermarkPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const navigate = useNavigate();
  const { theme } = useTheme();

  const logoUrl = theme === 'light' 
    ? 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7ade8283bc1015dd30.webp'
    : 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7a306550caec6c2c16.webp';

  // Check usage limits on mount
  useEffect(() => {
    const checkUsage = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('https://live-event-photos.onrender.com/api/admin/usage', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setUsage(data);
          
          // Check if at limit
          if (data.eventsLimit !== null && data.monthlyEvents >= data.totalEventsAllowed) {
            setLimitReached(true);
          }
        }
      } catch (error) {
        console.error('Error checking usage:', error);
      }
    };
    
    checkUsage();
  }, []);

  const handleWatermarkChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/png')) {
        alert('Please upload a PNG file');
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('Watermark file must be less than 2MB');
        return;
      }
      
      setWatermarkFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setWatermarkPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const removeWatermark = () => {
    setWatermarkFile(null);
    setWatermarkPreview(null);
  };

      const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !startTime || !endTime) {
      alert('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      
      // Upload watermark if provided
      let watermarkUrl = null;
      if (watermarkFile) {
        const formData = new FormData();
        formData.append('watermark', watermarkFile);
        
        try {
          const uploadResponse = await eventsAPI.uploadWatermark(formData);
          watermarkUrl = uploadResponse.data.url;
        } catch (uploadError) {
          console.error('Error uploading watermark:', uploadError);
          alert('Failed to upload watermark. Creating event without watermark.');
        }
      }
      
      const eventData = {
        name,
        start_time: new Date(startTime).getTime(),
        end_time: new Date(endTime).getTime(),
        send_thank_you_sms: sendThankYouSms,
        watermark_url: watermarkUrl,
      };
      console.log('Creating event with data:', eventData);
      const response = await eventsAPI.create(eventData);
      console.log('Event created:', response);
      navigate('/events');
    } catch (error) {
      console.error('Error creating event:', error);
      console.error('Error response:', error.response?.data);
      
      // Check if error is due to limit reached
      if (error.response?.data?.upgradeRequired) {
        setShowUpgradeModal(true);
      } else {
        alert(`Failed to create event: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="events-container">
      <ThemeToggle />
      <header className="events-header">
        <div>
          <img 
            src={logoUrl} 
            alt="EventPic Live" 
            style={{ height: '40px', marginBottom: '8px' }}
          />
          <h2>Create New Event</h2>
        </div>
        <button onClick={( ) => navigate('/events')} className="btn-secondary">
          Cancel
        </button>
      </header>

      <div className="events-content">
        {/* Limit Warning Banner */}
        {limitReached && (
          <div style={{
            maxWidth: '500px',
            margin: '0 auto 24px',
            padding: '16px',
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '8px',
            color: '#991b1b'
          }}>
            <strong>⚠️ Event Limit Reached</strong>
            <p style={{ margin: '8px 0 0', fontSize: '14px' }}>
              You've used {usage?.monthlyEvents} of {usage?.totalEventsAllowed} events this month.
              <button 
                onClick={() => setShowUpgradeModal(true)}
                style={{
                  marginLeft: '8px',
                  padding: '4px 12px',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Upgrade Plan
              </button>
            </p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--text)' }}>
              Event Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Summer Wedding"
              style={{ width: '100%', padding: '10px', fontSize: '16px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--text)' }}>
              Start Time
            </label>
            <input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              style={{ width: '100%', padding: '10px', fontSize: '16px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--text)' }}>
              End Time
            </label>
            <input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              style={{ width: '100%', padding: '10px', fontSize: '16px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'var(--text)' }}>
              Watermark (Optional)
            </label>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '10px' }}>
              Upload a PNG logo to appear at the bottom center of photos sent via MMS
            </div>
            
            {!watermarkPreview ? (
              <label style={{ 
                display: 'inline-block',
                padding: '10px 20px',
                backgroundColor: 'var(--card-bg)',
                border: '2px dashed var(--border-color)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}>
                <span style={{ color: 'var(--text)' }}>Choose PNG File</span>
                <input
                  type="file"
                  accept="image/png"
                  onChange={handleWatermarkChange}
                  style={{ display: 'none' }}
                />
              </label>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <img 
                  src={watermarkPreview} 
                  alt="Watermark preview" 
                  style={{ 
                    maxWidth: '150px', 
                    maxHeight: '80px', 
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    padding: '5px',
                    backgroundColor: '#f0f0f0'
                  }} 
                />
                <button 
                  type="button"
                  onClick={removeWatermark}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={sendThankYouSms}
                onChange={(e) => setSendThankYouSms(e.target.checked)}
                style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Send Thank You SMS</div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                  Automatically send attendees a thank you message with their photo gallery link when the event ends
                </div>
              </div>
            </label>
          </div>

          <button type="submit" className="btn-primary" disabled={loading} style={{ width: '100%' }}>
            {loading ? 'Creating...' : 'Create Event'}
          </button>
        </form>
      </div>
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        reason="You've reached your monthly event limit"
        currentTier={usage?.plan}
      />
    </div>
  );
}

export default NewEvent;
